import {
  ButtonDS,
  DialogModalDS,
  SelectDS,
  Spacer,
  TextCapitalized,
} from "@qivia/ui";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import "../../../i18n/i18n";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  HighMobilityConnectionPlan,
  highMobilityConnectionPlanSchema,
} from "@qivia/schemas-telemetry";
import { connectVehiclesToProviderAsync, selectConnectVehiclesToProviderStatus } from "../telemetrySlice";
import { VehicleDisplayed } from "../telemetryApi";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { useParams } from "react-router-dom";

type ConnectVehiclesToProviderModalProps = {
  visible: boolean;
  onClose: () => void;
  selectedVehicles: VehicleDisplayed[];
};

export const ConnectVehiclesToProviderModal = ({
  visible,
  onClose,
  selectedVehicles,
}: ConnectVehiclesToProviderModalProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const params = useParams();
  const companyUuid = params.id ?? "";
  const connectVehiclesToProviderStatus = useAppSelector(
    selectConnectVehiclesToProviderStatus,
  );
  const [selectedPlan, setSelectedPlan] =
    useState<HighMobilityConnectionPlan | undefined>(undefined);
  const [displayError, setDisplayError] = useState<boolean>(false);

  const planOptions = highMobilityConnectionPlanSchema.options.map((plan) => ({
    label: <TextCapitalized>{t(`telemetry.addVehiclesToProvider.modal.plan.${plan}`)}</TextCapitalized>,
    value: plan,
  }));

  const isProcessing = useMemo(() => {
    return connectVehiclesToProviderStatus === "processing";
  }, [connectVehiclesToProviderStatus]);

  const handleClose = useCallback(() => {
    setSelectedPlan(undefined);
    setDisplayError(false);
    onClose();
  }, [onClose]);

  const addVehiclesToProvider = useCallback(() => {
    if (!selectedPlan) {
      setDisplayError(true);
      return;
    }

    void dispatch(
      connectVehiclesToProviderAsync({
        vehicleUuids: selectedVehicles.map(
          (v) => (v as VehicleDisplayed & { id: string }).id,
        ),
        connectionPlan: selectedPlan,
        companyUuid,
      }),
    );
  }, [dispatch, selectedPlan, selectedVehicles, companyUuid]);

  useEffect(() => {
    if (connectVehiclesToProviderStatus === "success") {
      handleClose();
    }
  }, [connectVehiclesToProviderStatus, handleClose]);

  const handlePlanChange = useCallback(
    (option: { value: HighMobilityConnectionPlan } | null) => {
      setSelectedPlan(option?.value);
      setDisplayError(false);
    },
    [],
  );

  if (!companyUuid || !visible) return null;

  return (
    <DialogModalDS
      visible={visible}
      onClose={handleClose}
      title={t("telemetry.addVehiclesToProvider.modal.title")}
      width={35}
      bottomButton={
        <>
          <ButtonDS
            text={t("telemetry.addVehiclesToProvider.modal.cancelButton")}
            format="fill"
            buttonType="secondary"
            onClick={handleClose}
            disabled={isProcessing}
          />
          <Spacer x={1} />
          <ButtonDS
            text={t("telemetry.addVehiclesToProvider.modal.confirmButton")}
            format="fill"
            buttonType="primary"
            onClick={addVehiclesToProvider}
            disabled={!selectedPlan || isProcessing}
          />
        </>
      }
    >
      <StyledModalContent>
        <StyledDescription>
          <TextCapitalized>
            {t("telemetry.addVehiclesToProvider.modal.specific.rule")}
          </TextCapitalized>
        </StyledDescription>
        <Spacer y={2} />
        <SelectDS
          label={t("telemetry.addVehiclesToProvider.modal.plan.label")}
          value={planOptions.find((o) => o.value === selectedPlan)}
          options={planOptions}
          onChange={handlePlanChange}
          allWidth
          error={
            displayError
              ? t("telemetry.addVehiclesToProvider.modal.selectPlan.error")
              : undefined
          }
          disabled={isProcessing}
        />
      </StyledModalContent>
    </DialogModalDS>
  );
};

const StyledModalContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledDescription = styled.div`
  ${typographies["Body/M"]};
  color: ${colors["colors/text/black"]};
`;
